import React, { useEffect, useMemo, useState } from 'react';

import { fg } from '@confluence/feature-gating';
import { getPreferences } from '@atlassian/browser-storage-controls/controllers/preferences-controls/get-preferences';
import { PreferenceCategory } from '@atlassian/browser-storage-controls/types';
import { type ChatAction, HandleActionType } from '@atlassian/rovo-conversation-actions/types';

const CONVO_STARTER_NUDGE_ID = 'convo-starter';

/**
 * Checks whether the user has consented to Functional storage.
 * The convo starter nudge uses sessionStorage for suppression, which requires
 * Functional cookie consent per Atlassian policy.
 * Returns null while pending, true if granted, false if denied.
 */
function useCanShowConvoStarterNudge(): boolean | null {
	const [hasFunctionalConsent, setHasFunctionalConsent] = useState<boolean | null>(null);

	useEffect(() => {
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		if (!fg('rovo_button_convostarter_nudge_cookie_policy_check')) {
			setHasFunctionalConsent(true);
			return;
		}

		getPreferences()
			.then((preferences) => {
				setHasFunctionalConsent(Boolean(preferences?.[PreferenceCategory.Functional]));
			})
			.catch(() => {
				setHasFunctionalConsent(false);
			});
	}, []);

	return hasFunctionalConsent;
}

/**
 * Hook that creates and returns the convo starters nudge action placeholder.
 * The title and prompt are intentionally empty — they are enriched from the BE
 * recommendation payload at match time via enrichConversationStarterNudge in enrichNudge.
 *
 * Returns null when the rovo_button_convostarter_nudge_cookie_policy_check gate is on
 * and the user has not consented to Functional storage.
 */
export function useConvoStartersNudgeAction(): ChatAction | null {
	const hasFunctionalConsent = useCanShowConvoStarterNudge();

	return useMemo(() => {
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		if (!fg('aix_convo_starter_nudge_enabled')) {
			return null;
		}
		if (hasFunctionalConsent !== true) {
			return null;
		}
		return {
			id: CONVO_STARTER_NUDGE_ID,
			title: '',
			type: 'convo-starter-action',
			handleType: HandleActionType.Chat,
			icon: () => <></>,
			staticPrompt: '',
			starterType: 'static',
		};
	}, [hasFunctionalConsent]);
}
